import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="menu-loader"
export default class extends Controller {
  static targets = ["tab", "container", "loading"]
  static values = { venueSlug: String }

  connect() {
    this.boundShowMenu = this.showMenuFromEvent.bind(this)
    this.element.addEventListener("menu:show", this.boundShowMenu)

    // Load the first menu's line items on page load
    const firstTab = this.tabTargets[0]
    if (firstTab && this.hasContainerTarget) {
      this.loadMenuLineItems(firstTab.dataset.menuId)
    }
  }

  disconnect() {
    this.element.removeEventListener("menu:show", this.boundShowMenu)
  }

  loadMenu(event) {
    event.preventDefault()
    event.stopPropagation()

    const clickedTab = event.currentTarget
    const menuId = clickedTab.dataset.menuId

    // Don't reload if already active
    if (clickedTab.classList.contains("tab-active")) {
      return
    }

    this.setActiveTab(clickedTab)

    // Load menu line items
    this.loadMenuLineItems(menuId)
  }

  showMenuFromEvent(event) {
    const menuId = event.detail?.menuId
    if (!menuId) return

    const tab = this.tabTargets.find((target) => target.dataset.menuId === String(menuId))
    if (tab) {
      this.setActiveTab(tab)
    }

    this.showPanel(menuId)
  }

  async loadMenuLineItems(menuId) {
    const existingPanel = this.findPanel(menuId)
    if (existingPanel) {
      this.showPanel(menuId)
      this.dispatchLoaded(menuId)
      return
    }

    try {
      // Show loading indicator
      this.showLoading()
      this.clearError()

      // Make AJAX request to load menu line items
      const response = await fetch(`/venues/${this.venueSlugValue}/line_items/${menuId}`, {
        method: "GET",
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest",
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()

      const panel = document.createElement("div")
      panel.dataset.menuId = menuId
      panel.dataset.menuLoaderPanel = "true"
      panel.innerHTML = html

      this.containerTarget.appendChild(panel)
      this.showPanel(menuId)

      if (typeof window.lucide !== "undefined") {
        window.lucide.createIcons()
      }

      // Hide loading indicator
      this.hideLoading()

      // Trigger a custom event to notify other controllers that content has changed
      this.dispatchLoaded(menuId)
    } catch (error) {
      console.error("Error loading menu line items:", error)
      this.showError("Failed to load menu items. Please try again.")
      this.hideLoading()
    }
  }

  setActiveTab(activeTab) {
    this.tabTargets.forEach((tab) => {
      tab.classList.toggle("tab-active", tab === activeTab)
    })
  }

  findPanel(menuId) {
    return this.containerTarget.querySelector(
      `[data-menu-loader-panel="true"][data-menu-id="${menuId}"]`
    )
  }

  showPanel(menuId) {
    const panels = this.containerTarget.querySelectorAll("[data-menu-loader-panel='true']")

    panels.forEach((panel) => {
      panel.style.display = panel.dataset.menuId === String(menuId) ? "block" : "none"
    })
  }

  dispatchLoaded(menuId) {
    this.element.dispatchEvent(
      new CustomEvent("menu:loaded", {
        bubbles: true,
        detail: { menuId: menuId },
      })
    )
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = "block"
    }
    if (this.hasContainerTarget) {
      this.containerTarget.style.opacity = "0.5"
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = "none"
    }
    if (this.hasContainerTarget) {
      this.containerTarget.style.opacity = "1"
    }
  }

  showError(message) {
    if (this.hasContainerTarget) {
      this.clearError()

      const error = document.createElement("div")
      error.className = "alert alert-error menu-loader-error"
      error.innerHTML = `
        <i data-lucide="alert-circle" class="w-5 h-5"></i>
        <span>${message}</span>
      `
      this.containerTarget.appendChild(error)
    }
  }

  clearError() {
    this.containerTarget.querySelectorAll(".menu-loader-error").forEach((error) => {
      error.remove()
    })
  }
}
